//
// Copyright © 2026 Nester Labs
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type Employee } from '@hcengineering/contact'
import { type Class, type Doc, type Mixin, type Ref, type Timestamp } from '@hcengineering/core'
import { type Asset, type IntlString, plugin, type Plugin } from '@hcengineering/platform'
import { type TimeSpendReport } from '@hcengineering/tracker'
import { type AnyComponent } from '@hcengineering/ui'

/**
 * @public
 */
export const timesheetId = 'timesheet' as Plugin

/**
 * Lifecycle of one employee-week.
 *
 * `approved` is terminal for editing — the server trigger rejects any change to
 * time entries that fall inside an approved period.
 * @public
 */
export type TimesheetStatus = 'draft' | 'submitted' | 'approved' | 'rejected'

/**
 * A category of work, e.g. Development or Meeting. `defaultBillable` only seeds
 * the toggle when a row is created; the entry keeps its own flag afterwards.
 * @public
 */
export interface ActivityType extends Doc {
  name: string
  defaultBillable: boolean
  color: number
  archived?: boolean
}

/**
 * One employee's week on one project — the unit of submission and approval.
 *
 * `space` is the tracker Project, matching {@link ProjectTimeSpend}. That is
 * deliberate and load-bearing: approval is a project owner's job, so putting the
 * period in the project's space lets Huly's own space security keep one project's
 * hours away from another project's members, rather than relying on the UI to
 * filter. It also makes the key `(employee, weekStart, space)`, so no separate
 * project field is needed.
 *
 * A week spanning two projects is therefore two periods, each approved
 * independently by that project's owner. There is no single status for "the
 * employee's week" — that is inherent to per-project approval, not an accident.
 *
 * Hours are denormalized by the server trigger so the approval and report views
 * never have to aggregate entries client-side, and they count only the entries
 * belonging to this period's project.
 * @public
 */
export interface TimesheetPeriod extends Doc {
  employee: Ref<Employee>
  // Monday 00:00 and Sunday 23:59:59.999, both UTC-normalized — see getWeekBounds
  weekStart: Timestamp
  weekEnd: Timestamp

  status: TimesheetStatus
  submittedOn?: Timestamp
  approver?: Ref<Employee>
  approvedOn?: Timestamp
  comment?: string

  totalHours: number
  billableHours: number
}

/**
 * Classification mixin over tracker's existing time entry.
 *
 * Deliberately a mixin and not extra props on TimeSpendReport: it keeps this
 * feature out of models/tracker so merges from upstream stay clean.
 *
 * NOTE: no rate, currency or amount lives here, by design. Huly has no
 * field-level permission model, so anything stored is readable by any
 * workspace member. Hours are exported and priced outside the platform.
 * @public
 */
export interface ClassifiedTimeSpend extends TimeSpendReport {
  billable: boolean
  activity?: Ref<ActivityType>
}

/**
 * The shape the week grid works in. Both entry kinds satisfy it, so a row can
 * be rendered and summed without caring which class backs it.
 *
 * Field names deliberately mirror {@link TimeSpendReport} — `value` is man
 * hours, `date` is the day the work happened on (not when it was logged).
 * @public
 */
export interface TimeEntry {
  employee: Ref<Employee> | null
  date: Timestamp | null
  value: number
  description: string

  billable: boolean
  activity?: Ref<ActivityType>
}

/**
 * Time booked against a project as a whole, with no issue behind it — standing
 * meetings, on-call, code review, anything that is real project work but would
 * be noise as a tracker issue.
 *
 * `space` is the tracker Project, matching how tracker itself creates a
 * {@link TimeSpendReport} (in the issue's space). That is what lets the reports
 * group both kinds of entry by project without joining through issues.
 *
 * Unlike {@link ClassifiedTimeSpend} this is our own class, so the
 * classification fields sit on it directly rather than in a mixin.
 * @public
 */
export interface ProjectTimeSpend extends Doc, TimeEntry {
  employee: Ref<Employee>
  date: Timestamp
}

export default plugin(timesheetId, {
  app: {
    Timesheet: '' as Ref<Doc>
  },
  class: {
    ActivityType: '' as Ref<Class<ActivityType>>,
    TimesheetPeriod: '' as Ref<Class<TimesheetPeriod>>,
    ProjectTimeSpend: '' as Ref<Class<ProjectTimeSpend>>
  },
  mixin: {
    ClassifiedTimeSpend: '' as Ref<Mixin<ClassifiedTimeSpend>>
  },
  icon: {
    Timesheet: '' as Asset,
    MyWeek: '' as Asset,
    Team: '' as Asset,
    Approvals: '' as Asset,
    Reports: '' as Asset,
    Billable: '' as Asset
  },
  component: {
    MyWeek: '' as AnyComponent,
    TeamWeek: '' as AnyComponent,
    Approvals: '' as AnyComponent,
    Reports: '' as AnyComponent,
    ActivityTypePresenter: '' as AnyComponent,
    ActivityTypeRefPresenter: '' as AnyComponent,
    TimesheetStatusPresenter: '' as AnyComponent,
    RowTargetPresenter: '' as AnyComponent,
    RowTargetPopup: '' as AnyComponent,
    DayCellEditor: '' as AnyComponent,
    ProjectPresenter: '' as AnyComponent
  },
  ids: {
    Development: '' as Ref<ActivityType>,
    Meeting: '' as Ref<ActivityType>,
    Support: '' as Ref<ActivityType>,
    Internal: '' as Ref<ActivityType>
  },
  string: {
    Timesheet: '' as IntlString,
    ConfigLabel: '' as IntlString,
    ConfigDescription: '' as IntlString,

    MyWeek: '' as IntlString,
    Team: '' as IntlString,
    Approvals: '' as IntlString,
    Reports: '' as IntlString,

    ActivityType: '' as IntlString,
    ActivityTypes: '' as IntlString,
    NoActivity: '' as IntlString,
    Billable: '' as IntlString,
    NonBillable: '' as IntlString,
    BillableHours: '' as IntlString,
    NonBillableHours: '' as IntlString,
    TotalHours: '' as IntlString,

    Employee: '' as IntlString,
    Week: '' as IntlString,
    WeekStart: '' as IntlString,
    WeekEnd: '' as IntlString,
    Status: '' as IntlString,
    Approver: '' as IntlString,
    SubmittedOn: '' as IntlString,
    ApprovedOn: '' as IntlString,
    Comment: '' as IntlString,

    StatusDraft: '' as IntlString,
    StatusSubmitted: '' as IntlString,
    StatusApproved: '' as IntlString,
    StatusRejected: '' as IntlString,

    Submit: '' as IntlString,
    Approve: '' as IntlString,
    Reject: '' as IntlString,
    Withdraw: '' as IntlString,
    AddRow: '' as IntlString,
    PickIssue: '' as IntlString,
    PickProject: '' as IntlString,
    ProjectWork: '' as IntlString,
    WholeProject: '' as IntlString,
    RemoveRow: '' as IntlString,
    Description: '' as IntlString,
    Hours: '' as IntlString,
    Date: '' as IntlString,

    GroupBy: '' as IntlString,
    ByProject: '' as IntlString,
    ByEmployee: '' as IntlString,
    ByActivity: '' as IntlString,
    Daily: '' as IntlString,
    Weekly: '' as IntlString,
    NoData: '' as IntlString,
    NoEntries: '' as IntlString,
    ComingSoon: '' as IntlString,
    NoPendingApprovals: '' as IntlString,
    ApprovalsNotPermitted: '' as IntlString,
    NotProjectOwner: '' as IntlString,
    PendingApprovals: '' as IntlString,
    RecentlyDecided: '' as IntlString,
    NoTeamMembers: '' as IntlString,
    Export: '' as IntlString,
    Total: '' as IntlString,
    Project: '' as IntlString,
    PreviousWeek: '' as IntlString,
    NextWeek: '' as IntlString,
    ThisWeek: '' as IntlString,

    SubmitConfirm: '' as IntlString,
    RemoveRowConfirm: '' as IntlString,
    RejectReason: '' as IntlString,
    PeriodLocked: '' as IntlString,
    SubmittedNotification: '' as IntlString,
    DecidedNotification: '' as IntlString,

    Development: '' as IntlString,
    Meeting: '' as IntlString,
    Support: '' as IntlString,
    Internal: '' as IntlString
  }
})

export * from './utils'
